import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Star, Dot, Spiral } from "./doodles";

const lineWrap = {
  hidden: {},
  visible: (i) => ({
    transition: { staggerChildren: 0, delayChildren: 0.25 + i * 0.14 },
  }),
};
const lineInner = {
  hidden: { y: "115%" },
  visible: {
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
  },
};

function MaskedLine({ index, className = "", children }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className={`block ${className}`}
        variants={lineWrap}
        custom={index}
        initial="hidden"
        animate="visible"
      >
        <motion.span className="block" variants={lineInner}>
          {children}
        </motion.span>
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yNear = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 110]);
  const yFar = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -140]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="texture-grid-lg relative overflow-hidden pt-24 pb-12 md:pt-28 md:pb-16"    >
      {/* ---------- floating paper world ---------- */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <motion.div style={{ y: yFar }} className="absolute inset-0">
          {/* purple paper sheet */}
          <div
            className="float-a absolute right-[7%] top-[6%] hidden h-56 w-44 rounded-[1.75rem] border-2 border-navy bg-grape md:block"
            style={{ "--r": "6deg" }}
          >
            <div className="mt-10 ml-6 space-y-4">
              <div className="h-1.5 w-24 rounded-full bg-paper/70" />
              <div className="h-1.5 w-28 rounded-full bg-paper/70" />
              <div className="h-1.5 w-20 rounded-full bg-paper/70" />
            </div>
          </div>
          {/* pink paper sheet */}
          <div
            className="float-b absolute left-[5%] top-[54%] hidden h-44 w-36 rounded-[1.5rem] border-2 border-navy bg-blush lg:block"
            style={{ "--r": "-7deg" }}
          />
          {/* small cream sheet */}
          <div
            className="float-c absolute left-[4%] bottom-[12%] hidden h-36 w-44 rounded-[1.25rem] border-2 border-navy bg-paper md:block"
            style={{ "--r": "5deg" }}
          >
            <div className="mt-8 ml-5 space-y-3">
              <div className="h-1.5 w-24 rounded-full bg-navy/15" />
              <div className="h-1.5 w-28 rounded-full bg-navy/15" />
            </div>
          </div>
        </motion.div>

        <motion.div style={{ y: yNear }} className="absolute inset-0">
          <Star className="float-b absolute left-[8%] top-[24%] h-6 w-6 text-blush md:h-8 md:w-8" />
          <Star className="float-c absolute right-[38%] bottom-[18%] hidden h-6 w-6 text-grape md:block" />
          <Dot className="float-b absolute right-[8%] bottom-[30%] h-4 w-4 text-skyblue md:h-5 md:w-5" />
          <Dot className="float-c absolute left-[42%] top-[12%] hidden h-3 w-3 text-blush md:block" />
          <Spiral className="float-b absolute left-[14%] top-[52%] hidden h-12 w-12 text-skyblue lg:block" />
        </motion.div>
      </div>

      {/* ---------- headline ---------- */}
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 md:px-12 lg:px-20">
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mx-auto mb-6 flex w-fit -rotate-1 items-center gap-2 rounded-lg bg-skyblue px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-navy md:mb-8 md:text-sm"
          data-testid="hero-eyebrow"
        >
          <Star className="h-3.5 w-3.5 text-grape" aria-hidden="true" />
          Bello Paper — coming soon
        </motion.p>

        <h1
          className="text-center font-display text-[clamp(3.2rem,11vw,9rem)] font-semibold leading-[0.94] tracking-tight text-navy"
          data-testid="hero-headline"
        >
          <MaskedLine index={0}>What if</MaskedLine>
          <MaskedLine index={1}>
            <span className="relative inline-block px-[0.08em]">
              <motion.span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-[0.06em] top-[0.12em] -rotate-1 rounded-[0.18em] bg-blush"
                initial={reduce ? false : { scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformOrigin: "left center", zIndex: -1 }}
              />
              paper
            </span>
          </MaskedLine>
          <MaskedLine index={2}>
            could<span className="text-grape">…</span>
          </MaskedLine>
        </h1>
      </div>
    </section>
  );
}
